import _ from "lodash";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { adminRequests } from "@/api/(requests)/admin";
import BasicInfo from "@/features/member-profile/(medical)/components/BasicInfo";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import EmergencyInfo from "@/features/member-profile/(medical)/components/EmergencyInfo";
import FamilyMembersWidget from "@/components/Provider/PatientView/MemberProfile/Widget/FamilyMembersWidget";
import {
  formatInsuranceType,
  formatTimestamp,
  getFormattedStatus,
  getStatusClass,
  getStatusDefinition,
  showRawResponse,
} from "@/component-library/InsuranceEligibilityRecord/utils";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import ImageFullPreview from "@/components/Common/ImageFullPreview";
import { InsuranceEligibilityRecord } from "@/component-library";
import InsuranceInfo from "@/features/member-profile/(medical)/components/InsuranceInfo";
import { isAdminCSM, isAuthorized, isUserAdmin } from "@/utils/authToken";
import { parseErrorMessage } from "@/lib/helpers/parseErrorMessage";
import type { PatientDetails, UUID } from "@/core/types";
import { patientKeys } from "@/features/patient/api/keyFactory";
import { PRI, SEC } from "@/features/insurance/constants";
import { providerRequests } from "@/api/(requests)/provider";
import { uploadAssociatedFiles, UserFileTypes, viewPatientAssociatedFile } from "@/utils/fileUtil";
import UploadFile from "@/components/Provider/PatientView/UploadFile";
import { useMappedInsuranceList } from "@/features/insurance/api/queries/useMappedInsuranceList";
import { useNotesSummary } from "@/features/notes/(v2)/api/queries/useNotesSummary";
import { usePatientFiles } from "@/features/patient/api/queries/usePatientFiles";

import { MemberProfileConsentsSection } from "./components/MemberProfileConsentsSection";
import MemberProfileDischargeDataList from "./components/MemberProfileDischargeDataList";
import { MemberProfileFiles } from "./components/MemberProfileFiles";
import { MemberProfileMedicationManagementServices } from "./components/MemberProfileMedicationManagementServices";
import { useInsuranceImages } from "./hooks/useInsuranceImages";

type Props = {
  patientDetails: PatientDetails;
  refetchPatientDetails: () => void;
};

/** Core eligibility record extended with admin-only fields returned by fetch_insurance_eligibility_records */
type EligibilityDetails = Partial<NonNullable<PatientDetails["primary_eligibility_record"]>> & {
  insurance_details?: { group_id?: string };
  is_aso?: boolean;
};

type SubmitActions = { setSubmitting: (isSubmitting: boolean) => void };
type FieldSchema = Array<{ name?: string }>;

export function MemberProfileMedical(props: Props) {
  const dispatch = useDispatch();
  const { patientId } = useParams<{ patientId: UUID }>();
  const queryClient = useQueryClient();

  // admin_csm gets read-only access to this tab — see ASD-1378 / #5329
  const isReadOnlyForCSM = isAdminCSM();

  // State
  const [acknowledgements, setAcknowledgements] = useState<Record<string, unknown>>({});
  const [editBasicInfo, setEditBasicInfo] = useState(false);
  const [editEmergencyInfo, setEditEmergencyInfo] = useState(false);
  const [editPriIns, setEditPriIns] = useState(false);
  const [editSecIns, setEditSecIns] = useState(false);
  const [eligibilityDetails, setEligibilityDetails] = useState<EligibilityDetails>(
    props.patientDetails.primary_eligibility_record ?? {},
  );
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<File[]>([]);
  const [insFullPreviewContentType, setInsFullPreviewContentType] = useState<string | null>(null);
  const [insFullPreviewUrl, setInsFullPreviewUrl] = useState<string | null>(null);
  const [patientDetails, setPatientDetails] = useState<PatientDetails>(props.patientDetails);
  const [showUploadFileCard, setShowUploadFileCard] = useState(false);

  // Sync local patientDetails when the RQ cache delivers fresh data after refetchPatientDetails()
  useEffect(() => {
    setPatientDetails(props.patientDetails);
  }, [props.patientDetails]);

  // Use insurance images hook
  const {
    imageStatuses: insuranceImageStatuses,
    imageTypes: insuranceImageTypes,
    imageUrls: insuranceImageUrls,
  } = useInsuranceImages(patientDetails) as {
    imageStatuses: Record<string, string | null>;
    imageTypes: Record<string, string | null>;
    imageUrls: Record<string, string | null>;
  };

  // Fetch insurance list options
  const { data: { carrierOptions = [], stateOptions = [] } = {}, error: primaryError } =
    useMappedInsuranceList();

  const { data: { carrierOptions: secondaryCarrierOptions = [] } = {}, error: secondaryError } =
    useMappedInsuranceList({ isSecondary: true });

  const { data: notes } = useNotesSummary({ patientId });

  const { data: associatedFiles = [] } = usePatientFiles({
    patientId,
    select: (files) => files.filter((f) => f.file_sub_type === "ASSOCIATED_FILE_PATIENT"),
  });

  // Computed values
  const dischargeDataList = useMemo(() => {
    if (!notes) return null;

    return notes
      .filter((note) => note.note_type === "Discharge")
      .sort((a, b) =>
        moment(b.content?.discharge_date as string, "MM/DD/YYYY").diff(
          moment(a.content?.discharge_date as string, "MM/DD/YYYY"),
        ),
      );
  }, [notes]);

  // Fetch detailed eligibility for admins on mount / patient change
  const eligibilityRecordId = props.patientDetails?.primary_eligibility_record?.id;

  useEffect(() => {
    if (!isUserAdmin()) return;

    if (!eligibilityRecordId) return;

    adminRequests
      .fetch_insurance_eligibility_records({
        data: { id: eligibilityRecordId, query_type: "id" },
      })
      .then((res) => {
        if (res) setEligibilityDetails(res);
      })
      .catch((error) => {
        captureErrorMessage(parseError({ error, fallback: "Error fetching eligibility details" }), {
          tags: {
            action: "getEligibilityDetails",
            component: "MemberProfileMedical",
            errorType: "query",
            patientId,
          },
        });
      });
  }, [patientId, eligibilityRecordId]);

  // Log insurance list fetch errors
  useEffect(() => {
    if (primaryError) {
      console.error("Failed to load insurance options:", primaryError);
    }

    if (secondaryError) {
      console.error("Failed to load insurance options:", secondaryError);
    }
  }, [primaryError, secondaryError]);

  // Callbacks
  const addAcknowledgement = useCallback(
    (policyName: string, acknowledgement: unknown) => {
      const acks = patientDetails.acknowledgements as Record<string, unknown> | undefined;

      if (acks && !acks[policyName]) {
        setAcknowledgements((prev) => ({
          ...prev,
          [policyName]: acknowledgement,
        }));
      }
    },
    [patientDetails.acknowledgements],
  );

  const submitHandler = useCallback(
    async (
      formData: Record<string, unknown>,
      actions: SubmitActions,
      editForm: string,
      schema: FieldSchema,
    ) => {
      const data = { ...formData };
      const constructedData: Record<string, unknown> = {};

      schema.forEach((field) => {
        const name = field.name;

        if (name && name.split(".").length > 1) {
          const path = name.split(".");
          const value = _.get(data, path);

          _.setWith(constructedData, path, value);
        } else if (name) {
          constructedData[name] = formData[name];
        }
      });
      constructedData["username"] = formData["username"];
      const urlParams = { patientId };

      if (formData["mobile"] !== patientDetails.mobile) {
        if (patientDetails?.preferences?.is_sms_verified) {
          (data["preferences"] as Record<string, unknown>)["is_sms_verified"] = false;
        }
      }

      await providerRequests.update_patient_details({ data: constructedData, urlParams });
      actions.setSubmitting(false);

      setPatientDetails({ ...patientDetails, ...data } as PatientDetails);

      if (editForm === "editBasicInfo") setEditBasicInfo(false);
      if (editForm === "editEmergencyInfo") setEditEmergencyInfo(false);

      props.refetchPatientDetails();
    },
    [patientDetails, patientId, props],
  );

  const insuranceSubmitHandler = useCallback(
    async (formData: Record<string, unknown>, actions: SubmitActions) => {
      const data: Record<string, unknown> = { ...formData };
      const primaryIns = data.primary_insurance as Record<string, unknown> | undefined;

      data.acknowledgements = {
        ...patientDetails.acknowledgements,
        ...acknowledgements,
      };

      const insurance_data = {
        card_back: primaryIns?.card_back,
        card_front: primaryIns?.card_front,
        carrier: primaryIns?.carrier,
        dob: patientDetails?.dob,
        email: props.patientDetails.email,
        first_name: patientDetails.first_name,
        group_id: primaryIns?.group_id,
        last_name: patientDetails.last_name,
        member_id: primaryIns?.member_id,
        policy_dob: patientDetails.dob,
        policy_holder_name: primaryIns?.policy_holder_name,
        state: primaryIns?.state,
      };

      if (formData["primary_insurance"]) {
        await providerRequests
          .check_and_fetch_eligibility_record({ data: insurance_data })
          .then(async (data) => {
            const updated_insurance_check = data;

            setEligibilityDetails(updated_insurance_check);
          })
          .catch((err) => {
            console.error(err);
          });
      }

      data["username"] = patientId;
      const urlParams = { patientId };

      await providerRequests.update_patient_details({ data: data, urlParams });
      actions.setSubmitting(false);

      const updatedPatient = {
        ...patientDetails,
        ...formData,
        acknowledgements: data.acknowledgements,
      };

      if (formData["primary_insurance"]) {
        setEditPriIns((prev) => !prev);
      }

      if (formData["secondary_insurance"]) {
        setEditSecIns((prev) => !prev);
      }

      setPatientDetails(updatedPatient as PatientDetails);
      props.refetchPatientDetails();
    },
    [acknowledgements, patientDetails, patientId, props],
  );

  const onFileInputChange = useCallback((target: HTMLInputElement | null) => {
    if (target && target.files && target.files.length > 0) {
      setFilesToBeUploaded((prev) => {
        const newFiles = [...prev];

        for (const file of target.files!) {
          newFiles.push(file);
        }

        return newFiles;
      });
    }
  }, []);

  const onFileFileDelete = useCallback((index: number) => {
    setFilesToBeUploaded((prev) => {
      const changedFiles = [...prev];

      changedFiles.splice(index, 1);

      return changedFiles;
    });
  }, []);

  const onInsertFiles = useCallback(async () => {
    dispatch(showLoader());
    setShowUploadFileCard(false);

    try {
      await uploadAssociatedFiles(
        filesToBeUploaded,
        "patient",
        patientId,
        UserFileTypes.ASSOCIATED_FILE_PATIENT,
      );

      setFilesToBeUploaded([]);
      await queryClient.invalidateQueries({ queryKey: patientKeys.files(patientId) });
    } catch (err) {
      console.log(err);
      dispatch(
        // addToast is untyped JS — all fields are optional in practice
        (addToast as unknown as (p: { message?: string; messageType?: string }) => unknown)({
          message: parseErrorMessage(err),
          messageType: toastMessageTypes.error_v2,
        }),
      );
    }

    dispatch(hideLoader());
  }, [dispatch, filesToBeUploaded, patientId, queryClient]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <MemberProfileConsentsSection
        dob={patientDetails?.dob}
        insuranceCarrier={eligibilityDetails?.carrier}
        patientId={patientDetails?.username}
      />

      {showUploadFileCard && (
        <UploadFile
          files={filesToBeUploaded}
          handleDrop={onFileInputChange}
          hideModal={() => setShowUploadFileCard(false)}
          onFileRemove={onFileFileDelete}
          onInputChange={onFileInputChange}
          onInsertFiles={onInsertFiles}
        />
      )}

      {insFullPreviewUrl && (
        <ImageFullPreview
          close={() => setInsFullPreviewUrl(null)}
          contentType={insFullPreviewContentType}
          url={insFullPreviewUrl}
          wideView={false}
        />
      )}

      {props.patientDetails.medication_management_services && (
        <MemberProfileMedicationManagementServices />
      )}

      <MemberProfileFiles
        fileList={associatedFiles}
        getFile={(key: string) => viewPatientAssociatedFile(patientId, key)}
        showPopUp={isReadOnlyForCSM ? undefined : () => setShowUploadFileCard(true)}
        viewAllLink={`/app/patient/${patientId}/file`}
      />

      <BasicInfo
        {...props}
        data={patientDetails}
        edit={editBasicInfo}
        isReadOnly={isReadOnlyForCSM}
        submit={(formData: Record<string, unknown>, actions: SubmitActions, schema: FieldSchema) =>
          submitHandler(formData, actions, "editBasicInfo", schema)
        }
      />

      <FamilyMembersWidget {...props} />

      {notes && <MemberProfileDischargeDataList dischargeDataList={dischargeDataList} />}

      <EmergencyInfo
        {...props}
        data={patientDetails}
        edit={editEmergencyInfo}
        isReadOnly={isReadOnlyForCSM}
        submit={(formData: Record<string, unknown>, actions: SubmitActions, schema: FieldSchema) =>
          submitHandler(formData, actions, "editEmergencyInfo", schema)
        }
      />

      <InsuranceInfo
        {...props}
        addAcknowledgement={addAcknowledgement}
        carrierOptions={carrierOptions}
        className="tw:mb-0!"
        completedTitle="Insurance Information"
        data={{ username: patientId }}
        edit={editPriIns}
        editTitle="Your Insurance Information"
        emptyDetails="The member does not have any insurance information."
        emptyTitle="Add Insurance Information"
        groupId={eligibilityDetails?.insurance_details?.group_id}
        insurance={_.get(patientDetails, "primary_insurance")}
        insuranceImageStatuses={insuranceImageStatuses}
        insuranceImageUrls={insuranceImageUrls}
        isAso={eligibilityDetails?.is_aso}
        isEditing={editPriIns}
        onAddButtonClick={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns((prev) => !prev);
                setEditSecIns(false);
              }
        }
        onBackImageClick={() => {
          setInsFullPreviewContentType(insuranceImageTypes.pbType);
          setInsFullPreviewUrl(insuranceImageUrls.pbUrl);
        }}
        onCancelButtonClick={() => setEditPriIns((prev) => !prev)}
        onEdit={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns((prev) => !prev);
                setEditSecIns(false);
              }
        }
        onEditButtonClick={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns((prev) => !prev);
                setEditSecIns(false);
              }
        }
        onFrontImageClick={() => {
          setInsFullPreviewContentType(insuranceImageTypes.pfType);
          setInsFullPreviewUrl(insuranceImageUrls.pfUrl);
        }}
        stateOptions={stateOptions}
        submit={(formData: Record<string, unknown>, actions: SubmitActions) =>
          insuranceSubmitHandler(formData, actions)
        }
        type={PRI}
      />

      {eligibilityDetails && (
        <InsuranceEligibilityRecord
          allowRetry={false}
          carrier={eligibilityDetails.carrier}
          className={getStatusClass(eligibilityDetails.status)}
          copay={eligibilityDetails.copay}
          downloadResponseButtonOnClick={() => showRawResponse(eligibilityDetails)}
          errors={eligibilityDetails.errors}
          insuranceType={formatInsuranceType(eligibilityDetails.insurance_type)}
          numberOfAttempts={eligibilityDetails.number_of_attempts}
          plan={eligibilityDetails.plan}
          rawResponseKey={eligibilityDetails.raw_response_key}
          retryButtonOnClick={() => {}}
          showDownloadRawResponse={
            isAuthorized("admin:customer_success_executive") &&
            eligibilityDetails.raw_response_exists
          }
          status={getFormattedStatus(eligibilityDetails.status)}
          statusDefinition={getStatusDefinition(eligibilityDetails.status)}
          timestamp={formatTimestamp(eligibilityDetails.timestamp)}
          transactionId={eligibilityDetails.control_number}
        />
      )}

      <InsuranceInfo
        {...props}
        addAcknowledgement={addAcknowledgement}
        carrierOptions={secondaryCarrierOptions}
        completedTitle="Secondary Insurance Information"
        data={{
          username: patientId,
        }}
        edit={editSecIns}
        editTitle="Secondary Insurance Information"
        emptyDetails="The member does not have any insurance information."
        emptyTitle="Add Secondary Insurance Information"
        insurance={_.get(patientDetails, "secondary_insurance")}
        insuranceImageStatuses={insuranceImageStatuses}
        insuranceImageUrls={insuranceImageUrls}
        isEditing={editSecIns}
        onAddButtonClick={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns(false);
                setEditSecIns((prev) => !prev);
              }
        }
        onBackImageClick={() => {
          setInsFullPreviewContentType(insuranceImageTypes.sbType);
          setInsFullPreviewUrl(insuranceImageUrls.sbUrl);
        }}
        onCancelButtonClick={() => setEditSecIns((prev) => !prev)}
        onEdit={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns(false);
                setEditSecIns((prev) => !prev);
              }
        }
        onEditButtonClick={
          isReadOnlyForCSM
            ? undefined
            : () => {
                setEditPriIns(false);
                setEditSecIns((prev) => !prev);
              }
        }
        onFrontImageClick={() => {
          setInsFullPreviewContentType(insuranceImageTypes.sfType);
          setInsFullPreviewUrl(insuranceImageUrls.sfUrl);
        }}
        stateOptions={stateOptions}
        submit={(formData: Record<string, unknown>, actions: SubmitActions) =>
          insuranceSubmitHandler(formData, actions)
        }
        type={SEC}
      />
    </div>
  );
}
